import { FC } from 'react';
import { useFragment } from 'react-relay';
import { graphql, RelayProp } from 'react-relay/legacy';
import {
    SharedRelatedItemsWrapper,
    ChildrenProps as GridProps,
} from '../../shared/SharedRelatedItemsWrapper/SharedRelatedItemsWrapper';
import { SharedMobileRelatedItem } from '../../shared/SharedMobileRelatedItem/SharedMobileRelatedItem';
import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile';
import {
    trackItemsImpression,
    trackItemClick,
    Item as ItemTrackProps,
} from '../../shared/SharedRelatedItemsWrapper/helpers/sharedRelatedItemsWrapperTracking';
import { peopleAlsoBrowsed } from '../../shared/sharedMessages';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy';
import { RESULTS_FIRST_ROW_COUNT } from '../../constants/sbConstants';
import { useUserSessionRegions } from 'dibs-regional-info/exports/regionalInfoHelpers';

import { BuySharedPersonalizedSearchItems_viewer$key } from './__generated__/BuySharedPersonalizedSearchItems_viewer.graphql';
import { BuySharedPersonalizedSearchItems_itemSearch$key } from './__generated__/BuySharedPersonalizedSearchItems_itemSearch.graphql';
import { BuySharedPersonalizedSearchItems_user$key } from './__generated__/BuySharedPersonalizedSearchItems_user.graphql';

type Props = {
    viewer: BuySharedPersonalizedSearchItems_viewer$key;
    itemSearch: BuySharedPersonalizedSearchItems_itemSearch$key;
    user: BuySharedPersonalizedSearchItems_user$key;
    isMobile: boolean;
    isClient: boolean;
    relay: RelayProp;
};

export const BuySharedPersonalizedSearchItems: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    user: userRef,
    isMobile,
    isClient,
    relay,
}) => {
    const viewer = useFragment(
        graphql`
            fragment BuySharedPersonalizedSearchItems_viewer on Viewer {
                ...SharedItemTile_viewer
            }
        `,
        viewerRef
    );

    const itemSearch = useFragment(
        graphql`
            fragment BuySharedPersonalizedSearchItems_itemSearch on ItemSearchQueryConnection
            @argumentDefinitions(
                userCountryCode: { type: "String", defaultValue: "" }
                priceBookName: { type: "String" }
            ) {
                ...SharedItemTile_itemSearch
                personalizedSearchItems(first: 28) {
                    edges {
                        node {
                            item {
                                serviceId
                                ...SharedMobileRelatedItem_item
                                    @arguments(
                                        userCountryCode: $userCountryCode
                                        priceBookName: $priceBookName
                                    )
                                ...SharedItemTile_item
                                    @arguments(
                                        isTrade: $isTrade
                                        userCountryCode: $userCountryCode
                                        priceBookName: $priceBookName
                                        showSeller: $showSeller
                                    )
                                ...sharedRelatedItemsWrapperTracking_item
                            }
                        }
                    }
                }
            }
        `,
        itemSearchRef
    );

    const user = useFragment(
        graphql`
            fragment BuySharedPersonalizedSearchItems_user on User {
                ...SharedRelatedItemsWrapper_user
                ...SharedMobileRelatedItem_user
                ...SharedItemTile_user
            }
        `,
        userRef
    );

    const { personalizedSearchItems } = itemSearch;
    const items = personalizedSearchItems?.edges?.map(itemNode => itemNode?.node?.item);
    const itemIds = items?.map(item => item?.serviceId) || [];
    const userRegions = useUserSessionRegions() || [];

    const onItemClick = ({ item, index }: { item: ItemTrackProps; index: number }): void => {
        trackItemClick({
            items: [item],
            startIndex: index,
            list: `Buy personalized search items`,
            userRegions,
        });
    };

    if (!itemIds.length) {
        return null;
    }

    return (
        <SharedRelatedItemsWrapper
            user={user}
            header={peopleAlsoBrowsed}
            relay={relay}
            isMobile={isMobile}
            isClient={isClient}
            itemIds={itemIds}
            showSwiper={isMobile}
            initialCount={RESULTS_FIRST_ROW_COUNT}
            onItemsImpression={({ startIndex, itemsLengthToShow }) => {
                if (items?.length) {
                    trackItemsImpression({
                        items: [...items].splice(startIndex, itemsLengthToShow),
                        startIndex,
                        list: `Buy personalized search items`,
                        userRegions,
                    });
                }
            }}
        >
            {({ itemClassName, ...itemProps }: GridProps) =>
                items?.filter(filterFalsy).map((item, index) => {
                    return isMobile ? (
                        <SharedMobileRelatedItem
                            key={item.serviceId}
                            index={index}
                            item={item}
                            user={user}
                            ymalHeartsEnabled
                            onClick={() => onItemClick({ item, index })}
                            {...itemProps}
                            {...itemClassName(index)}
                        />
                    ) : (
                        <SharedItemTile
                            key={item.serviceId}
                            index={index}
                            viewer={viewer}
                            item={item}
                            itemSearch={itemSearch}
                            user={user}
                            showCreatorName
                            showSellerName={false}
                            isClient={isClient}
                            fetchFolderOnMouseOver
                            isMobile={false}
                            onClick={() => onItemClick({ item, index })}
                            {...itemProps}
                            {...itemClassName(index)}
                        />
                    );
                })
            }
        </SharedRelatedItemsWrapper>
    );
};
